import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { PaperPlaneTilt, ChatCircleDots, Flag, Prohibit, ArrowLeft, User } from '@phosphor-icons/react';
import { PRO_TOKEN_KEY } from '@/pages/wellness/WellnessProLoginPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MessagingInbox — dual-role messaging widget.
 * Props:
 *  - role: 'pro' | 'customer'
 *  - customerEmail: (customer role only) email of the user viewing inbox
 * For pro role, uses Bearer token from localStorage.
 */
const MessagingInbox = ({ role, customerEmail }) => {
  const [threads, setThreads] = useState([]);
  const [activePeer, setActivePeer] = useState(null); // peer id (email for pro, professional_id for customer)
  const [activeMeta, setActiveMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const scrollRef = useRef(null);

  const authHeader = () => {
    if (role === 'pro') return { Authorization: `Bearer ${localStorage.getItem(PRO_TOKEN_KEY) || ''}` };
    return {};
  };

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const url = role === 'pro'
        ? `${BACKEND_URL}/api/wellness/pro/threads`
        : `${BACKEND_URL}/api/wellness/customer/threads?email=${encodeURIComponent(customerEmail)}`;
      const res = await axios.get(url, { headers: authHeader() });
      setThreads(res.data);
    } catch (err) { setThreads([]); }
    finally { setLoadingThreads(false); }
  }, [role, customerEmail]);

  const openThread = useCallback(async (thread) => {
    const peer = role === 'pro' ? thread.customer_email : thread.professional_id;
    setActivePeer(peer);
    setActiveMeta(thread);
    try {
      const url = role === 'pro'
        ? `${BACKEND_URL}/api/wellness/messages/threads/${encodeURIComponent(peer)}`
        : `${BACKEND_URL}/api/wellness/messages/threads/${peer}?viewer_email=${encodeURIComponent(customerEmail)}`;
      const res = await axios.get(url, { headers: authHeader() });
      setMessages(res.data.messages || []);
      // Mark read
      const professional_id = role === 'pro' ? (await getProId()) : peer;
      const cust_email = role === 'pro' ? peer : customerEmail;
      await axios.post(`${BACKEND_URL}/api/wellness/messages/mark-read`,
        { professional_id, customer_email: cust_email },
        { headers: authHeader() }
      );
      loadThreads();
    } catch (err) { toast.error('Failed to load thread'); }
  }, [role, customerEmail, loadThreads]);

  const getProId = async () => {
    if (role !== 'pro') return null;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/wellness/pro/me`, { headers: authHeader() });
      return res.data.professional_id;
    } catch { return null; }
  };

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const professional_id = role === 'pro' ? (await getProId()) : activePeer;
      const cust_email = role === 'pro' ? activePeer : customerEmail;
      await axios.post(`${BACKEND_URL}/api/wellness/messages/send`,
        { professional_id, customer_email: cust_email, text: text.trim() },
        { headers: authHeader() }
      );
      setText('');
      openThread(activeMeta);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };

  const reportUser = async () => {
    const reason = window.prompt('Reason for report:');
    if (!reason) return;
    try {
      const professional_id = role === 'pro' ? (await getProId()) : activePeer;
      const cust_email = role === 'pro' ? activePeer : customerEmail;
      await axios.post(`${BACKEND_URL}/api/wellness/messages/report`, {
        professional_id, customer_email: cust_email, reason, reporter_role: role,
      });
      toast.success('Reported. Nova will review this thread.');
    } catch (err) { toast.error('Failed to report'); }
  };

  const blockUser = async () => {
    if (role !== 'pro') { toast.info('Please contact Nova support to block.'); return; }
    if (!window.confirm('Block this conversation? They will no longer be able to message you.')) return;
    try {
      const professional_id = await getProId();
      await axios.post(`${BACKEND_URL}/api/wellness/messages/block`, {
        professional_id, customer_email: activePeer,
      }, { headers: authHeader() });
      toast.success('Blocked');
      setActivePeer(null);
      loadThreads();
    } catch (err) { toast.error('Failed to block'); }
  };

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  return (
    <div className="bg-white rounded-3xl border border-stone-100 overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr] min-h-[500px]" data-testid={`messaging-inbox-${role}`}>
      {/* Threads list */}
      <aside className={`border-r border-stone-100 ${activePeer ? 'hidden md:block' : 'block'}`}>
        <div className="p-4 border-b border-stone-100">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500">Conversations</p>
        </div>
        {loadingThreads ? (
          <div className="p-4 text-sm text-stone-500">Loading...</div>
        ) : threads.length === 0 ? (
          <div className="p-6 text-center text-sm text-stone-500">
            <ChatCircleDots size={26} weight="duotone" className="text-stone-300 mx-auto mb-2" />
            No conversations yet
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {threads.map((t) => {
              const peerId = role === 'pro' ? t.customer_email : t.professional_id;
              const displayName = role === 'pro' ? (t.customer_name || t.customer_email) : (t.professional_name || 'Professional');
              const secondary = role === 'pro' ? t.customer_email : t.professional_type;
              const isActive = activePeer === peerId;
              return (
                <button
                  key={peerId}
                  onClick={() => openThread(t)}
                  className={`w-full text-left p-4 hover:bg-stone-50 transition-all ${isActive ? 'bg-[#F0F7F1]' : ''}`}
                  data-testid={`thread-${peerId}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="font-semibold text-sm text-[#1C1917] truncate">{displayName}</p>
                    {t.unread > 0 && <span className="bg-[#C05C3D] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid={`unread-${peerId}`}>{t.unread}</span>}
                  </div>
                  <p className="text-xs text-stone-500 truncate">{secondary}</p>
                  <p className="text-xs text-stone-600 truncate mt-1">{t.last_sender === role ? 'You: ' : ''}{t.last_text}</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">{new Date(t.last_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* Thread */}
      <div className={`flex flex-col ${activePeer ? 'block' : 'hidden md:flex md:items-center md:justify-center'}`}>
        {!activePeer ? (
          <div className="text-stone-400 text-center p-8">
            <ChatCircleDots size={32} weight="duotone" className="mx-auto mb-2" />
            Select a conversation
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setActivePeer(null)} className="md:hidden text-stone-500"><ArrowLeft size={18} weight="bold" /></button>
                <div className="w-10 h-10 rounded-full bg-[#F0F7F1] flex items-center justify-center"><User size={20} weight="duotone" className="text-[#1A4331]" /></div>
                <div>
                  <p className="font-semibold text-sm text-[#1C1917]">{role === 'pro' ? (activeMeta?.customer_name || activeMeta?.customer_email) : activeMeta?.professional_name}</p>
                  <p className="text-xs text-stone-500">{role === 'pro' ? activeMeta?.customer_email : activeMeta?.professional_type}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={reportUser} className="text-stone-500 hover:text-red-600 p-1.5 rounded-full hover:bg-red-50" title="Report"><Flag size={16} weight="duotone" /></button>
                {role === 'pro' && <button onClick={blockUser} className="text-stone-500 hover:text-red-600 p-1.5 rounded-full hover:bg-red-50" title="Block"><Prohibit size={16} weight="duotone" /></button>}
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[400px]" data-testid="thread-messages">
              {messages.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-6">No messages yet. Say hello!</p>
              ) : messages.map((m) => {
                const mine = m.sender === role;
                return (
                  <motion.div key={m.message_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.message_id}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-[#1A4331] text-white' : 'bg-stone-100 text-stone-900'}`}>
                      <p className="text-sm whitespace-pre-wrap">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${mine ? 'text-emerald-100' : 'text-stone-500'}`}>{new Date(m.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="p-4 border-t border-stone-100 flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                placeholder="Type a message..."
                className="flex-1 bg-stone-50 border border-stone-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#1A4331]"
                data-testid="msg-input"
              />
              <button onClick={sendMessage} disabled={sending || !text.trim()} className="bg-[#1A4331] text-white px-4 py-2 rounded-full disabled:opacity-40" data-testid="msg-send-btn">
                <PaperPlaneTilt size={18} weight="fill" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagingInbox;
